<template>
  <div class="product-listing-block sm:py-56 relative">
    <h2 v-if="component.title" class="text-center mb-24">
      {{ component.title }}
    </h2>

    <div
      v-if="hasMultipleTabs"
      class="flex md:block container xxl:px-24 -mx-6 md:mx-auto"
    >
      <div class="w-full container">
        <div class="flex flex-wrap mb-24 sm:mb-32 justify-center items-center">
          <div
            v-for="tab in tabs"
            :key="tab.tab_title"
            class="w-auto p-4 flex-shrink-0 mb-4"
          >
            <TabSelector
              class="px-4 sm:px-6"
              :text="tab.tab_title"
              :selected="currentActiveTab === tab.tab_title"
              fullwidth
              @click="getActiveProducts(tab.tab_title)"
            />
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="loading"
      class="bg-main absolute inset-0 flex justify-center pt-224 opacity-50"
    >
      <fa
        class="text-black-full text-xl"
        :icon="['fal', 'spinner']"
        :spin="true"
      />
    </div>
    <component
      :is="uiStore.isMobile ? Swiper : 'div'"
      v-else
      :slides-per-view="2.5"
      :slides-offset-before="24"
      :slides-offset-after="24"
      :space-between="24"
      class="md-container md:flex md:flex-wrap md:justify-center"
    >
      <component
        :is="uiStore.isMobile ? SwiperSlide : 'div'"
        v-for="(product, index) in products"
        :key="product.id"
        class="md:w-1/4 sm:px-12"
      >
        <div
          :key="currentActiveTab"
          data-product-tab="5"
          itemscope
          itemtype="https://schema.org/ItemList"
        >
          <ProductSpot 
            v-if="!isSeller || product.brandName === 'Ullmax'"
            itemprop="itemListElement"
            :product="product"
            :index="index"
            :language="currentLanguage()"
            class="w-full"
            :list-title="componentTitle"
          />
        </div>
      </component>
    </component>
  </div>
</template>

<script setup lang="ts">
import type ComponentBlock from '~/models/component-block';
import Product from '~/models/product';
import type { EcomProduct, ProductTab } from '~/constants/types/product';

import { useUiStore } from '~/store/ui';
import { useConfigStore } from '~/store/config';
import TabSelector from '~/components/generic/TabSelector.vue';
import ProductSpot from '~/components/productlist/ProductSpot.vue';
const config = useConfigStore();
const uiStore = useUiStore();
const { sellerCookieOptions } = useCookieConfig();

const isSeller = useCookie<boolean>('isSeller', sellerCookieOptions);

const Swiper = resolveComponent('Swiper');
const SwiperSlide = resolveComponent('SwiperSlide');

const props = defineProps<{
  component: ComponentBlock,
}>();

const currentActiveTab = ref('');
const products = ref<Product[]>([]);
const loading = ref(false);

const tabs = computed<ProductTab[]>(()=> {
  return props.component.products as ProductTab[];
});

const hasMultipleTabs = computed<boolean>(()=> {
  return tabs.value.length > 1;
});

const componentTitle = computed<string>(()=> {
  return props.component.title || props.component.layout;
});

const sortedProducts = computed<Product[]>(()=> {
  const sortedProducts = [...products.value].sort((a, z) => {
    const aTitle = a.title.toUpperCase();
    const zTitle = z.title.toUpperCase();
    if (aTitle < zTitle) {
      return -1;
    }
    if (aTitle > zTitle) {
      return 1;
    }
    return 0;
  });

  return sortedProducts;
});

const { fetchProducts } = useProductService();

const getActiveProducts = async(tab: string) => {
  const productsIn = props.component.products as ProductTab[];
  const tabProducts = productsIn.find((product) => tab === product.tab_title);

  if (tabProducts && tabProducts.ecom_products) {
    const ids =
      tabProducts.ecom_products?.map((product) =>
        product.ecom_id.toString()
      ) ?? [];
    try {
      const timeout = setTimeout(() => (loading.value = true), 250);
      const { products: loadedProducts } = await fetchProducts({
        products: ids,
      });
      clearTimeout(timeout);
      currentActiveTab.value = tab;

      // Sort products the same order as in wp admin
      const output = [] as EcomProduct[];
      tabProducts.ecom_products?.forEach((tp) => {
        const thisProduct = loadedProducts.find(
          (p: EcomProduct) => Number(p.product) === Number(tp.ecom_id)
        );
        if (thisProduct) {
          output.push(thisProduct);
        } else {
          console.log('Nothing found');
        }
      });
      products.value = output.map(
        (p: EcomProduct) => new Product({ contentData: null, ecomData: p })
      );

      /*
      if (
        this.reportedProductTabs.length > 0 &&
        !this.reportedProductTabs.includes(this.currentActiveTab)
      ) {
        this.sendGtmImpressions();
      }*/
    } catch (e) {
      console.log(e);
    } finally {
      loading.value = false;
    }
  } else {
    products.value = [];
  }
};

const currentLanguage = () => {
  const currentLanguage = config.market.language;
  return currentLanguage;
};

onMounted(async()=> {
  setTimeout(async()=> {
    const products = props.component.products as ProductTab[];
    await getActiveProducts(products[0].tab_title);
    currentActiveTab.value =
      props.component.products.length > 0
        ? (props.component.products as ProductTab[])[0].tab_title
        : '';
  }, 500);
});

</script>

<style scoped lang="postcss">
.product-listing-block {
  .product-spot-regular {
    @apply w-full !important;
  }
}
</style>
